/**
 * 认证状态管理 Store
 * 管理用户登录状态、Token、用户信息等
 */

import { create } from 'zustand';
import { createPersistedStore } from '../utils/persist';
import type { VerifyResult } from '@/api';

/**
 * 认证状态接口
 */
interface AuthState {
  // ==================== 状态 ====================
  
  /** 是否已登录 */
  isAuthenticated: boolean;
  /** Access Token */
  accessToken: string | null;
  /** Refresh Token */
  refreshToken: string | null;
  /** 用户信息 */
  userInfo: VerifyResult | null;
  /** API Key（用于应用调用） */
  apiKey: string | null;

  // ==================== Actions ====================
  
  /**
   * 设置登录状态
   * @param data - 登录结果数据
   */
  setAuth: (data: VerifyResult) => void;

  /**
   * 设置 Access Token
   * @param token - Access Token
   */
  setAccessToken: (token: string) => void;

  /**
   * 设置 Refresh Token
   * @param token - Refresh Token
   */
  setRefreshToken: (token: string) => void;

  /**
   * 设置 API Key
   * @param key - API Key
   */
  setApiKey: (key: string) => void;

  /**
   * 登出
   * 清除所有认证信息
   */
  logout: () => void;

  /**
   * 检查是否已登录
   * @returns 是否已登录
   */
  checkAuth: () => boolean;
}

/**
 * 初始状态
 */
const initialState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  userInfo: null,
  apiKey: null,
};

/**
 * 认证 Store
 * 
 * @example
 * ```ts
 * import { useAuthStore } from '@/stores';
 * 
 * function MyComponent() {
 *   const { isAuthenticated, userInfo, logout } = useAuthStore();
 *   
 *   if (!isAuthenticated) {
 *     return <LoginButton />;
 *   }
 *   
 *   return <div>欢迎, {userInfo?.userId}</div>;
 * }
 * ```
 */
export const useAuthStore = create<AuthState>()(
  createPersistedStore(
    (set, get) => ({
      ...initialState,

      setAuth: (data) => {
        set({
          isAuthenticated: true,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          userInfo: data,
        });
      },

      setAccessToken: (token) => {
        set({ accessToken: token });
      },

      setRefreshToken: (token) => {
        set({ refreshToken: token });
      },

      setApiKey: (key) => {
        set({ apiKey: key });
      },

      logout: () => {
        set(initialState);
      },

      checkAuth: () => {
        const state = get();
        return state.isAuthenticated && !!state.accessToken;
      },
    }),
    {
      name: 'auth-storage',
      // 只持久化关键字段，排除方法
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        userInfo: state.userInfo,
        apiKey: state.apiKey,
      }),
    }
  )
);

/**
 * 便捷 Hooks
 */

/**
 * 获取当前用户信息
 */
export const useUserInfo = () => useAuthStore((state) => state.userInfo);

/**
 * 获取登录状态
 */
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);

/**
 * 获取 Access Token
 */
export const useAccessToken = () => useAuthStore((state) => state.accessToken);

/**
 * 获取 API Key
 */
export const useApiKey = () => useAuthStore((state) => state.apiKey);
